<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import { useSectorsStore } from 'dashboard/stores/sectors';
import { useProductsStore } from 'dashboard/stores/products';
import { useProductPlansStore } from 'dashboard/stores/productPlans';
import { useAddOnsStore } from 'dashboard/stores/addOns';
import { useProposalsStore } from 'dashboard/stores/proposals';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  contact: { type: Object, default: null },
});

const emit = defineEmits(['created']);

const { t } = useI18n();
const sectorsStore = useSectorsStore();
const productsStore = useProductsStore();
const productPlansStore = useProductPlansStore();
const addOnsStore = useAddOnsStore();
const proposalsStore = useProposalsStore();

const dialogRef = ref(null);
const STEPS = props.contact
  ? ['sector', 'product', 'plan', 'addons', 'review']
  : ['contact', 'sector', 'product', 'plan', 'addons', 'review'];
const stepIndex = ref(0);
const currentStep = computed(() => STEPS[stepIndex.value]);
const createdProposal = ref(null);
const isSubmitting = ref(false);

// --- contact search (only when not launched from a Contact page) ---
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(props.contact);
const searchContacts = async () => {
  if (!contactQuery.value.trim()) {
    contactResults.value = [];
    return;
  }
  const {
    data: { payload },
  } = await ContactAPI.search(contactQuery.value.trim());
  contactResults.value = payload;
};
let searchTimeout = null;
watch(contactQuery, () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(searchContacts, 300);
});
const pickContact = contact => {
  selectedContact.value = contact;
};

// --- catalog selections ---
const selectedSectorId = ref('');
const selectedProductId = ref('');
const selectedPlanId = ref('');
const selectedAddOnIds = ref([]);
const discountPercent = ref('0');

const sectorOptions = computed(() =>
  sectorsStore.records
    .filter(s => s.active)
    .map(s => ({ value: s.id, label: s.name }))
);
const productOptions = computed(() =>
  productsStore.records
    .filter(p => p.active && p.sector_id === Number(selectedSectorId.value))
    .map(p => ({ value: p.id, label: p.name }))
);
const planOptions = computed(() =>
  productPlansStore.records
    .filter(
      pl => pl.active && pl.product_id === Number(selectedProductId.value)
    )
    .map(pl => ({ value: pl.id, label: pl.name }))
);
const availableAddOns = computed(() =>
  addOnsStore.records.filter(
    a => a.active && a.product_id === Number(selectedProductId.value)
  )
);
const selectedPlan = computed(() =>
  productPlansStore.records.find(pl => pl.id === Number(selectedPlanId.value))
);
const selectedProduct = computed(() =>
  productsStore.records.find(p => p.id === Number(selectedProductId.value))
);

watch(selectedProductId, productId => {
  selectedPlanId.value = '';
  selectedAddOnIds.value = [];
  if (productId) {
    productPlansStore.get({ productId });
    addOnsStore.get({ productId });
  }
});

const toggleAddOn = id => {
  const index = selectedAddOnIds.value.indexOf(id);
  if (index === -1) selectedAddOnIds.value.push(id);
  else selectedAddOnIds.value.splice(index, 1);
};

// --- Year 1 / Year 2+ pricing preview (review step, before creation) ---
// Always resolved server-side through the same Proposals::PricingCalculator
// #create uses, so this preview can never drift from the real total.
const pricingPreview = ref(null);
const isPreviewLoading = ref(false);
const previewError = ref('');

const fetchPreview = async () => {
  if (!selectedPlanId.value) return;
  isPreviewLoading.value = true;
  previewError.value = '';
  pricingPreview.value = null;
  try {
    pricingPreview.value = await proposalsStore.previewPricing({
      product_plan_id: Number(selectedPlanId.value),
      add_on_ids: selectedAddOnIds.value,
      discount_percent: Number(discountPercent.value) || 0,
    });
  } catch (error) {
    previewError.value =
      error?.message || t('PROPOSALS_MGMT.WIZARD.PREVIEW_ERROR');
  } finally {
    isPreviewLoading.value = false;
  }
};

watch(currentStep, step => {
  if (step === 'review' && !createdProposal.value) fetchPreview();
});

const canGoNext = computed(() => {
  switch (currentStep.value) {
    case 'contact':
      return !!selectedContact.value?.id;
    case 'sector':
      return !!selectedSectorId.value;
    case 'product':
      return !!selectedProductId.value;
    case 'plan':
      return !!selectedPlanId.value;
    default:
      return true;
  }
});

const goNext = () => {
  if (!canGoNext.value) return;
  if (stepIndex.value < STEPS.length - 1) stepIndex.value += 1;
};
const goBack = () => {
  if (stepIndex.value > 0) stepIndex.value -= 1;
};

const submitProposal = async () => {
  isSubmitting.value = true;
  try {
    const proposal = await proposalsStore.create({
      proposal: {
        contact_id: selectedContact.value.id,
        product_plan_id: Number(selectedPlanId.value),
        add_on_ids: selectedAddOnIds.value,
        discount_percent: Number(discountPercent.value) || 0,
      },
    });
    createdProposal.value = proposal;
    emit('created', proposal);
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.WIZARD.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const sendEmail = async () => {
  try {
    createdProposal.value = await proposalsStore.sendEmail(
      createdProposal.value.id
    );
    useAlert(t('PROPOSALS_MGMT.SEND_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.SEND_ERROR'));
  }
};

const sendWhatsapp = async () => {
  try {
    createdProposal.value = await proposalsStore.sendWhatsapp(
      createdProposal.value.id
    );
    useAlert(t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_ERROR'));
  }
};

const resetWizard = () => {
  stepIndex.value = 0;
  selectedContact.value = props.contact;
  contactQuery.value = '';
  contactResults.value = [];
  selectedSectorId.value = '';
  selectedProductId.value = '';
  selectedPlanId.value = '';
  selectedAddOnIds.value = [];
  discountPercent.value = '0';
  createdProposal.value = null;
  pricingPreview.value = null;
  previewError.value = '';
};

const open = () => {
  resetWizard();
  sectorsStore.get();
  productsStore.get();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="resetWizard"
  >
    <div class="flex flex-col w-full gap-5">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('PROPOSALS_MGMT.WIZARD.TITLE') }}
      </h3>

      <div v-if="currentStep === 'contact'" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.CONTACT_STEP_LABEL') }}
        </p>
        <Input
          v-model="contactQuery"
          :placeholder="t('PROPOSALS_MGMT.WIZARD.CONTACT_SEARCH_PLACEHOLDER')"
          autofocus
        />
        <ul class="flex flex-col gap-1 overflow-y-auto max-h-56">
          <li
            v-for="result in contactResults"
            :key="result.id"
            class="p-2 text-sm rounded-lg cursor-pointer hover:bg-n-slate-3"
            :class="{ 'bg-n-blue-2': selectedContact?.id === result.id }"
            @click="pickContact(result)"
          >
            {{ result.name }}
            <span class="text-n-slate-11">{{
              result.email || result.phone_number
            }}</span>
          </li>
        </ul>
      </div>

      <div v-else-if="currentStep === 'sector'" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.SECTOR_STEP_LABEL') }}
        </p>
        <SelectInput
          v-model="selectedSectorId"
          :options="sectorOptions"
          :placeholder="t('PROPOSALS_MGMT.WIZARD.SECTOR_PLACEHOLDER')"
        />
      </div>

      <div v-else-if="currentStep === 'product'" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.PRODUCT_STEP_LABEL') }}
        </p>
        <SelectInput
          v-model="selectedProductId"
          :options="productOptions"
          :placeholder="t('PROPOSALS_MGMT.WIZARD.PRODUCT_PLACEHOLDER')"
        />
      </div>

      <div v-else-if="currentStep === 'plan'" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.PLAN_STEP_LABEL') }}
        </p>
        <SelectInput
          v-model="selectedPlanId"
          :options="planOptions"
          :placeholder="t('PROPOSALS_MGMT.WIZARD.PLAN_PLACEHOLDER')"
        />
      </div>

      <div v-else-if="currentStep === 'addons'" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.ADDONS_STEP_LABEL') }}
        </p>
        <label
          v-for="addOn in availableAddOns"
          :key="addOn.id"
          class="flex items-center gap-2 p-2 text-sm rounded-lg hover:bg-n-slate-3"
        >
          <input
            type="checkbox"
            :checked="selectedAddOnIds.includes(addOn.id)"
            @change="toggleAddOn(addOn.id)"
          />
          {{ addOn.name }}
          <span class="text-n-slate-11">({{ addOn.price }})</span>
        </label>
        <p v-if="!availableAddOns.length" class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.WIZARD.NO_ADDONS') }}
        </p>
        <Input
          v-model="discountPercent"
          type="number"
          :label="t('PROPOSALS_MGMT.WIZARD.DISCOUNT_PERCENT')"
        />
      </div>

      <div v-else-if="currentStep === 'review'" class="flex flex-col gap-4">
        <template v-if="!createdProposal">
          <p class="text-sm text-n-slate-11">
            {{ t('PROPOSALS_MGMT.WIZARD.REVIEW_LABEL') }}
          </p>
          <div class="p-4 text-sm rounded-lg bg-n-slate-2">
            <p>
              <strong>{{ t('PROPOSALS_MGMT.WIZARD.REVIEW_CONTACT') }}</strong>
              {{ selectedContact?.name }}
            </p>
            <p>
              <strong>{{ t('PROPOSALS_MGMT.WIZARD.REVIEW_PRODUCT') }}</strong>
              {{ selectedProduct?.name }} - {{ selectedPlan?.name }}
            </p>
            <p>
              <strong>{{ t('PROPOSALS_MGMT.WIZARD.REVIEW_ADDONS') }}</strong>
              {{ selectedAddOnIds.length }}
            </p>
            <p>
              <strong>{{ t('PROPOSALS_MGMT.WIZARD.REVIEW_DISCOUNT') }}</strong>
              {{ discountPercent }}%
            </p>
          </div>

          <p v-if="isPreviewLoading" class="text-sm text-n-slate-11">
            {{ t('PROPOSALS_MGMT.WIZARD.PREVIEW_LOADING') }}
          </p>
          <p v-else-if="previewError" class="text-sm text-n-ruby-11">
            {{ previewError }}
          </p>
          <div
            v-else-if="pricingPreview"
            class="flex flex-col gap-2 p-4 text-sm rounded-lg bg-n-slate-2"
          >
            <div>
              <p class="text-xs font-medium uppercase text-n-slate-11">
                {{ t('PROPOSALS_MGMT.WIZARD.PREVIEW_YEAR_1') }}
              </p>
              <p class="text-base font-medium text-n-slate-12">
                {{ pricingPreview.currency }}
                {{ pricingPreview.year_1_total }}
              </p>
            </div>
            <div v-if="Number(pricingPreview.recurring_annual_total) > 0">
              <p class="text-xs font-medium uppercase text-n-slate-11">
                {{ t('PROPOSALS_MGMT.WIZARD.PREVIEW_YEAR_2') }}
              </p>
              <p class="text-base font-medium text-n-slate-12">
                {{ pricingPreview.currency }}
                {{ pricingPreview.recurring_annual_total }}
                <span class="text-xs font-normal text-n-slate-11">{{
                  t('PROPOSALS_MGMT.PER_YEAR')
                }}</span>
              </p>
              <p class="text-xs text-n-slate-11">
                {{ pricingPreview.currency }}
                {{ pricingPreview.recurring_total }} /
                {{ pricingPreview.recurring_interval }}
              </p>
            </div>
          </div>

          <Button
            :label="t('PROPOSALS_MGMT.WIZARD.GENERATE')"
            :is-loading="isSubmitting"
            @click="submitProposal"
          />
        </template>
        <template v-else>
          <div class="p-4 text-sm rounded-lg bg-n-teal-2">
            <p class="font-medium text-n-slate-12">
              {{ createdProposal.reference }}
              {{ t('PROPOSALS_MGMT.WIZARD.CREATED_SUFFIX') }}
            </p>
            <p>
              {{ t('PROPOSALS_MGMT.WIZARD.YEAR_1_TOTAL') }}
              {{ createdProposal.currency }} {{ createdProposal.year_1_total }}
            </p>
            <p v-if="Number(createdProposal.recurring_total) > 0">
              {{ t('PROPOSALS_MGMT.WIZARD.RECURRING') }}
              {{ createdProposal.currency }}
              {{ createdProposal.recurring_total }} /
              {{ createdProposal.recurring_interval }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <a
              v-if="createdProposal.pdf_url"
              :href="createdProposal.pdf_url"
              target="_blank"
              rel="noopener noreferrer"
              class="px-3 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-slate-3"
            >
              {{ t('PROPOSALS_MGMT.DOWNLOAD_PDF') }}
            </a>
            <Button
              v-if="!createdProposal.email_sent_at"
              :label="t('PROPOSALS_MGMT.WIZARD.SEND_BY_EMAIL')"
              @click="sendEmail"
            />
            <span v-else class="text-sm text-n-teal-11">{{
              t('PROPOSALS_MGMT.WIZARD.ALREADY_SENT_EMAIL')
            }}</span>
            <Button
              v-if="!createdProposal.whatsapp_sent_at"
              :label="t('PROPOSALS_MGMT.WIZARD.SEND_BY_WHATSAPP')"
              variant="outline"
              @click="sendWhatsapp"
            />
            <span v-else class="text-sm text-n-teal-11">{{
              t('PROPOSALS_MGMT.WIZARD.ALREADY_SENT_WHATSAPP')
            }}</span>
          </div>
        </template>
      </div>

      <div class="flex justify-between pt-2">
        <Button
          v-if="stepIndex > 0 && !createdProposal"
          :label="t('PROPOSALS_MGMT.WIZARD.BACK')"
          variant="link"
          @click="goBack"
        />
        <span v-else />
        <Button
          v-if="currentStep !== 'review'"
          :label="t('PROPOSALS_MGMT.WIZARD.NEXT')"
          :disabled="!canGoNext"
          @click="goNext"
        />
      </div>
    </div>
  </Dialog>
</template>
